import {Injectable} from '@angular/core';
import {Observable, of, switchMap} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {CredentialsService} from './credentials.service';
import {PermissionService} from '~local/app/shared/services/PermissionService';
import {map, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {AuthenticationServiceBase, isStatusResponseSuccess, StatusResponse} from '~shared/types/authentication.types';

export type LoginData = {
    email: string;
    password: string;
};

type Response = {
    token: string,
    authorizeViaEmail: boolean,
    id: number
};

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService extends AuthenticationServiceBase {
    constructor(
        private readonly credentialsService: CredentialsService,
        private readonly httpClient: HttpClient,
        private readonly permissionService: PermissionService,
        private readonly router: Router,
    ) {
        super();
        this.isAuthorized$ = this.credentialsService.credentials$.pipe(map($ => !!$));
    }

    public login($data: LoginData): Observable<Response> {
        const body = {
            email: $data.email,
            password: $data.password,
            deviceInfo: {
                browser: 'none',
                platform: 'none',
                hash: $data.email
            }
        };

        return this.httpClient.post<Response>('/admin/auth/login', body).pipe(
            tap((response) => {
                const data = {
                    email: $data.email,
                    token: response.token,
                    id: response.id
                };

                this.credentialsService.setCredentials(data);
            })
        );
    }

    public logout() {
        this.credentialsService.setCredentials();
        this.router.navigateByUrl('/login');
    }

    public isAuthenticated(): Observable<boolean> {
        return this.isAuthorized$.pipe(
            switchMap(($isAuthenticated) => {
                if (!$isAuthenticated) {
                    return of($isAuthenticated);
                }
                return this.getStatus().pipe(map($statusRes => isStatusResponseSuccess($statusRes)));
            })
        );
    }

    private getStatus(): Observable<StatusResponse> {
        return this.httpClient.get<StatusResponse>('/auth/status').pipe(
            tap($res => {
                if (isStatusResponseSuccess($res)) {
                    this.permissionService.setPermissions($res.roles, $res.permissions, $res.source);
                }
            })
        );
    }
}
